import { z } from 'zod';

import { Envs } from '../config';

export const EnvName = z.enum(Envs);

/** `GET /health` (docs/03 §2). */
export const HealthResponse = z.object({
  ok: z.literal(true),
  env: EnvName,
  version: z.string(),
});
export type HealthResponse = z.infer<typeof HealthResponse>;

/** `GET /config` public config (docs/03 §2), loaded by the web apps at boot. */
export const PublicConfig = z.object({
  env: EnvName,
  firebase: z.object({
    apiKey: z.string(),
    authDomain: z.string(),
    databaseURL: z.string(),
    projectId: z.string(),
    appId: z.string(),
    /**
     * WP5.2: `host:port` of the Firebase **Auth emulator**, emitted by `GET /config` only when
     * the API runs with `APP_ENV=local` and `FIREBASE_AUTH_EMULATOR_HOST` set. The browser
     * clients call `connectAuthEmulator` when it is present, which is what makes the hermetic
     * `tests/e2e` local stack possible (no Firebase project, no network).
     */
    authEmulatorHost: z.string().optional(),
  }),
  mixpanelToken: z.string(),
  stripePublishableKey: z.string(),
  mediaBase: z.string(),
  /**
   * WP5.4 — the origin every user-facing link points at (`app.acapella.lol`): the pairing and
   * guest-join URLs the TV renders as a QR, and the CTA of the transactional emails. Server-side
   * it is `WEB_BASE`; the apps read it here so a host rename is a var change, not a rebuild.
   */
  webBase: z.string(),
  supportEmail: z.string(),
  appVersionMin: z.string(),
  /** TV player HUD auto-hide (docs/04 §4). Optional until the settings table exists. */
  controlsTimeoutMs: z.number().int().positive().optional(),
  /**
   * Player engine for clear HLS packages on the TV (2026-10-23 stick test): `hlsjs` (default
   * when absent) or `shaka`. A var on the API, so the installed APKs switch without a reinstall.
   */
  hlsEngine: z.enum(['hlsjs', 'shaka']).optional(),
  /**
   * APK 1.2.0+ plays through its native ExoPlayer unless this is `false` (API `NATIVE_PLAYER`
   * = `off`): the kill switch, so the TVs fall back to the web players without a reinstall.
   */
  nativePlayer: z.boolean().optional(),
  /**
   * Standalone web (docs/13 §5.8). `checkoutEnabled` needs `settings.billing.checkout_enabled` and
   * both Stripe secrets. Optional for older APIs: a missing object reads as "checkout off", so the
   * Premium buttons say "Próximamente".
   */
  web: z
    .object({
      checkoutEnabled: z.boolean(),
      /** `POST /billing/card-session` exists: "Actualizar tarjeta" opens in the site's modal. */
      cardModalEnabled: z.boolean().optional(),
    })
    .optional(),
});
export type PublicConfig = z.infer<typeof PublicConfig>;
